/**
 * WB Packing Slip, the packing work order (docs/designs/packaging-module.md, P2; legacy seedtrac
 * Packaging login "WB Packing Slip"). Planned and prioritised on the desk, released to the packing
 * floor, packed on the Packing Slip or the handheld, then reviewed here.
 *
 * Round 1 is frontend only: these are the endpoints the backend will serve.
 */
import { api } from "@/lib/api-client";

export const WORK_ORDER_FORM = "packing_work_order";
/** Its twin for packed lots: the Re-Printing Packing Slip (legacy "Re-Printing Packing Slip"). */
export const REPRINT_ORDER_FORM = "reprint_work_order";
/** Which of the two work orders a page shows: WB Packing Slip or Re-Printing Packing Slip. */
export type WorkOrderKind = "wb" | "reprint";

/**
 * Draft → Preview → Released (Pending on the floor) → Completed when its Packing Slip is submitted.
 * Cancel frees the picked seed.
 */
export type WorkOrderStatus =
  "draft" | "previewed" | "released" | "completed" | "cancelled";
export type WorkOrderPriority = "high" | "medium" | "low";
export type PackLineType = "manual" | "online";
/** SeedTrac's Manual Type, for a Manual Pack Line Type. */
export type ManualType = "manual" | "machine";
export type UpsType = "ST" | "NST";

export type WorkOrderHeader = {
  cropId?: number | null;
  varietyId?: number | "coded" | "none" | null;
  stageId?: number | null;
  indentNo?: string | null;
  indentDate?: string | null;
  plannedOn?: string | null;
  priority?: WorkOrderPriority | null;
  machineId?: number | null;
  operatorId?: number | null;
  treatmentId?: number | null;
  upstype?: UpsType | null;
  lineType?: PackLineType | null;
  /** Old slips only: the pack line is chosen on the handheld now (user 2026-11-02). */
  dominoLineId?: number | null;
  manualType?: ManualType | null;
  remarks?: string | null;
};

export interface WorkOrderLine {
  id: number;
  lineNo: number;
  lotId: number;
  lotNumber: string;
  crop: string;
  variety: string;
  /** The whole lot when it was posted (legacy "Existing NoB / Qty"). */
  lotBags: number;
  lotQty: string;
  /** Picked for packing: NoB, and the Qty for Packing. */
  bags: number;
  qty: string;
  mode: "entire" | "partial";
  qcStatus: string | null;
  dot: string | null;
  dosf: string | null;
  /** Where the picked seed sits, "WH-01/A01/1 · 3 NoB · 120.000". */
  sloc: string[];
  /** The packed lot it will become (C → P), from the lot-number engine. */
  packedLotNumber: string | null;
  /** Filled once the Packing Slip is submitted. */
  packedQty: string | null;
  pouches: number | null;
  /** Root Stock WB Packing Slip: seeds planned, and seeds packed once its Packing Slip is in. */
  plannedNos: number | null;
  packedNos: number | null;
  /** Root stock: what the pack floor counted, once its job there is finalised (user 2026-11-02). */
  floorCount?: FloorCount | null;
  /** Packing Details from the desk (user 2026-11-02); null on lots posted before. */
  pack?: WorkOrderPack | null;
  /** The pack line the handheld chose for this lot, and that line's next label. */
  dominoLine?: string | null;
  nextLabel?: string | null;
}

export interface WorkOrderPack {
  validityMonths: number;
  qcDateType: "dot" | "dosf";
  validUpto: string | null;
  validityDays: number | null;
  pouchCcGrams: number;
  balanceKg: string | null;
  packSizeId: number;
  upsLabel: string;
  upsGrams: number;
  nosPerPouch: number | null;
  pouchesInWb: number;
  wbKg: string;
  wbInMp: number;
  mpKg: string;
  mrpPerUps: string | null;
  mrpPerGram: string | null;
  rs: {
    upsGrams: number;
    nosPerPouch: number;
    pouchesInWb: number;
    wbInMp: number;
  } | null;
}

export interface FloorCount {
  jobId: number;
  packSizeId: number;
  upsGrams: number;
  nosPerPouch: number;
  pouches: number;
  nos: number;
  packedKg: string;
  /** The lot's own boxing on the floor (root stock is non-standardized). */
  pouchesInWb: number;
  wbInMp: number;
}

export interface WorkOrder {
  id: number;
  docNo: string | null;
  date: string;
  status: WorkOrderStatus;
  version: number;
  facilityId: number;
  header: WorkOrderHeader & {
    crop?: string;
    variety?: string;
    dominoLine?: string | null;
    packingSlipId?: number | null;
    packingSlipNo?: string | null;
    /** The next pouch label on its domino line this year, e.g. DAY0081425 (P3). */
    nextLabel?: string | null;
    /** A root stock crop: each lot has its Planned Nos, and its Packing Slip counts them. */
    rootStock?: boolean;
  };
  remarks: string | null;
  cancelReason: string | null;
  lines: WorkOrderLine[];
}

export interface WorkOrderListRow {
  id: number;
  docNo: string | null;
  date: string;
  status: WorkOrderStatus;
  header: WorkOrder["header"];
  lines: Pick<
    WorkOrderLine,
    | "lotNumber"
    | "crop"
    | "variety"
    | "bags"
    | "qty"
    | "mode"
    | "packedQty"
    | "pouches"
  >[];
  /** Pack seed put in PSW bins yet (the legacy list's "SLOC Updation"). */
  slocUpdated: boolean;
}

export interface PageMeta {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export type WorkOrderQuery = {
  facilityId?: number | undefined;
  from?: string | undefined;
  to?: string | undefined;
};

const qs = (p: Record<string, string | number | undefined | null>) => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(p))
    if (v !== undefined && v !== null && v !== "") s.set(k, String(v));
  const q = s.toString();
  return q ? `?${q}` : "";
};
const BASES: Record<WorkOrderKind, string> = {
  wb: "/packaging/work-orders",
  reprint: "/packaging/reprint-orders",
};

/** The same calls for either work order; only the path differs. */
export const workOrderCallsFor = (kind: WorkOrderKind) => {
  const BASE = BASES[kind];
  return {
    list: (
      q: WorkOrderQuery & { tab: WorkOrderStatus; page: number; limit: number }
    ) => api<{ items: WorkOrderListRow[]; meta: PageMeta }>(`${BASE}${qs(q)}`),
    counts: (q: WorkOrderQuery) =>
      api<Record<WorkOrderStatus, number>>(`${BASE}/counts${qs(q)}`),
    get: (id: number) => api<WorkOrder>(`${BASE}/${id}`),
    create: (body: {
      facilityId?: number | undefined;
      clientRequestId: string;
      header: WorkOrderHeader;
      line: Record<string, unknown>;
    }) => api<WorkOrder>(BASE, { method: "POST", json: body }),
    saveHeader: (
      id: number,
      body: WorkOrderHeader & { expectedVersion: number }
    ) => api<WorkOrder>(`${BASE}/${id}`, { method: "PUT", json: body }),
    addLine: (
      id: number,
      body: { expectedVersion: number; line: Record<string, unknown> }
    ) => api<WorkOrder>(`${BASE}/${id}/lines`, { method: "POST", json: body }),
    removeLine: (id: number, lineId: number, expectedVersion: number) =>
      api<WorkOrder>(
        `${BASE}/${id}/lines/${lineId}${qs({ expectedVersion })}`,
        {
          method: "DELETE",
        }
      ),
    step: (
      id: number,
      step: "preview" | "edit" | "release",
      expectedVersion: number
    ) =>
      api<WorkOrder>(`${BASE}/${id}/${step}`, {
        method: "POST",
        json: { expectedVersion },
      }),
    cancel: (
      id: number,
      body: {
        reason: string;
        expectedVersion: number;
        stepUpToken?: string | undefined;
      }
    ) =>
      api<WorkOrder>(`${BASE}/${id}/cancel`, {
        method: "POST",
        json: { reason: body.reason, expectedVersion: body.expectedVersion },
        ...(body.stepUpToken
          ? { headers: { "x-step-up-token": body.stepUpToken } }
          : {}),
      }),
  };
};
export const workOrderCalls = workOrderCallsFor("wb");

/* ── Packing Slip (P3): a released WB Packing Slip packed into pouches, C → P ───────────── */

export const PACKING_SLIP_FORM = "packing_slip";
export type PackingSlipStatus =
  "draft" | "previewed" | "submitted" | "cancelled";

export type PackingSlipHeader = {
  workOrderId?: number | null;
  packedOn?: string | null;
  refNo?: string | null;
  remarks?: string | null;
};

/**
 * An NST (non-standard) UPS, typed on the slip: no crop pack size and no Pack Layout behind it
 * (legacy "NST Packing Slip", map §1.3). Window boxes do not apply (D21). When the row is converted to
 * master packs the operator types only the MP's GROSS weight and picks its MP Type (D20, D22); the
 * pouches per MP are derived: floor((gross − tare) ÷ UPS), in whole grams, the tare from the MP Types
 * master. This is what the Post Item Form sends (`line.ups[].nst`).
 */
export interface NstUpsPost {
  /** The typed UPS in whole grams only (D24): 12 g → 12, 5.050 kg → 5050. */
  upsGrams: number;
  /** The unit it was typed in, so it reads back the same way (legacy Gms. / Kgs.). */
  unit: "g" | "kg";
  /** MP Types master row; null when the row is not converted to MP. */
  mpTypeId: number | null;
  /** Gross MP weight (seed + carton / bag), kg with 3 decimals; null when not converted. */
  mpGrossKg: string | null;
}

/** An NST row as the server returns it: what was posted, plus the tare and the derived pouches per MP. */
export interface NstUps extends NstUpsPost {
  /** The MP Type as it was at Post ("Carton 5 kg"), and its tare then; null when not converted. */
  mpTypeName: string | null;
  tareKg: string | null;
  /** floor((gross − tare) ÷ UPS) in whole grams; null when not converted. */
  perMp: number | null;
}

/**
 * A row of the MP Types master (D22): what a master pack is packed in and its tare. The slip reads
 * the plant's active rows through a packaging read (packing users hold no masters.* code), as with
 * the Pack Layouts.
 */
/** An MP Type's pack: a carton or a bag. */
export type MpType = "carton" | "bag";
export interface PackMpType {
  id: number;
  code: string;
  name: string;
  mpType: MpType;
  /** kg, 3 decimals. */
  tareKg: string;
  facilityId: number;
  isActive: boolean;
}

export interface PackingUps {
  /** ST: the crop's pack size. NST: the pack size the server found or made for the typed grams. */
  packSizeId: number;
  label: string;
  qty: string;
  pouches: number;
  labelFrom: string | null;
  labelTo: string | null;
  toMp: boolean;
  mps: number | null;
  /** Window boxes from the Pack Layout: all of them, and those not inside a master pack. */
  wbs: number;
  looseWbs: number;
  /** Pouches outside any master pack. */
  loosePouches: number;
  /** Set on an NST slip's rows only; `wbs` and `looseWbs` are then 0. */
  nst?: NstUps | null;
  /** Root stock rows only: seeds in the row (pouches x seeds per packet). */
  nos?: number | null;
}

/**
 * One UPS row as the Post Item Form posts it (`line.ups[]`). ST sends the crop's pack size; NST sends
 * `packSizeId: null` and the typed size in `nst`. The server recounts pouches and master packs.
 */
export interface PackingUpsPost {
  packSizeId: number | null;
  /** Must be whole pouches exactly (D23): qty = pouches × UPS; extra grams go in Packing Loss. */
  qty: number;
  labelFrom: string;
  labelTo: string;
  toMp: boolean;
  mps: number;
  nst?: NstUpsPost;
  /** Root stock only: the pouches counted (their kg is weighed, not worked out). */
  pouches?: number;
  /** Root stock only: the packet weight and the seeds in it, typed per lot. */
  rs?: { upsGrams: number; nosPerPouch: number };
}

export interface PackingSlipLine {
  id: number;
  lineNo: number;
  lotId: number;
  lotNumber: string;
  /** The work order's pick: what this lot brings to the line. */
  qty: string;
  /** The work order's pick: the whole lot (E) or part of it (P). */
  mode: "entire" | "partial";
  packLoss: string;
  captive: string;
  packedQty: string;
  validityMonths: number;
  qcDateType: "dot" | "dosf";
  validUpto: string | null;
  ups: PackingUps[];
  /** Where the pouches went, "PSW-01/A01/1 · 400 pouches · 20.000". */
  packSloc: string[];
  packedLotNumber: string | null;
  pouches: number;
  /** Root stock: the seeds packed. */
  packedNos?: number | null;
}

export interface PackingSlip {
  id: number;
  docNo: string | null;
  date: string;
  status: PackingSlipStatus;
  version: number;
  facilityId: number;
  header: PackingSlipHeader & {
    workOrderNo?: string | null;
    crop?: string | null;
    variety?: string | null;
    dominoLine?: string | null;
    lineType?: PackLineType | null;
    upstype?: UpsType | null;
    /** Packs a Root Stock WB Packing Slip (TRP): counted in Nos. */
    rootStock?: boolean;
  };
  remarks: string | null;
  cancelReason: string | null;
  lines: PackingSlipLine[];
}

export interface PackingSlipListRow {
  id: number;
  docNo: string | null;
  date: string;
  status: PackingSlipStatus;
  header: PackingSlip["header"];
  lines: Pick<
    PackingSlipLine,
    | "lotNumber"
    | "qty"
    | "mode"
    | "packLoss"
    | "captive"
    | "packedQty"
    | "ups"
    | "pouches"
    | "packedLotNumber"
  >[];
}

/** A crop's pack layout (Pack Layouts master): how its pouches of one size are boxed. */
export interface PackLayout {
  id: number;
  cropId: number;
  packSizeId: number;
  upstype: UpsType;
  pouchesInWb: number;
  wbWeightKg: string;
  wbInMp: number;
  mpWeightKg: string;
  maxMp: number | null;
}

/** The list and its counts, optionally one pack type (the work order's `upstype`). */
export type PackingSlipQuery = WorkOrderQuery & {
  upstype?: UpsType | undefined;
};

const PS = "/packaging/packing-slips";

export const packingSlipCalls = {
  list: (
    q: PackingSlipQuery & {
      tab: PackingSlipStatus;
      page: number;
      limit: number;
    }
  ) => api<{ items: PackingSlipListRow[]; meta: PageMeta }>(`${PS}${qs(q)}`),
  counts: (q: PackingSlipQuery) =>
    api<Record<PackingSlipStatus, number>>(`${PS}/counts${qs(q)}`),
  get: (id: number) => api<PackingSlip>(`${PS}/${id}`),
  create: (body: {
    facilityId?: number | undefined;
    clientRequestId: string;
    header: PackingSlipHeader;
    line: Record<string, unknown>;
  }) => api<PackingSlip>(PS, { method: "POST", json: body }),
  saveHeader: (
    id: number,
    body: PackingSlipHeader & { expectedVersion: number }
  ) => api<PackingSlip>(`${PS}/${id}`, { method: "PUT", json: body }),
  addLine: (
    id: number,
    body: { expectedVersion: number; line: Record<string, unknown> }
  ) => api<PackingSlip>(`${PS}/${id}/lines`, { method: "POST", json: body }),
  removeLine: (id: number, lineId: number, expectedVersion: number) =>
    api<PackingSlip>(`${PS}/${id}/lines/${lineId}${qs({ expectedVersion })}`, {
      method: "DELETE",
    }),
  step: (
    id: number,
    step: "preview" | "edit" | "submit",
    expectedVersion: number
  ) =>
    api<PackingSlip>(`${PS}/${id}/${step}`, {
      method: "POST",
      json: { expectedVersion },
    }),
  cancel: (
    id: number,
    body: {
      reason: string;
      expectedVersion: number;
      stepUpToken?: string | undefined;
    }
  ) =>
    api<PackingSlip>(`${PS}/${id}/cancel`, {
      method: "POST",
      json: { reason: body.reason, expectedVersion: body.expectedVersion },
      ...(body.stepUpToken
        ? { headers: { "x-step-up-token": body.stepUpToken } }
        : {}),
    }),
  layouts: (cropId: number, varietyId?: number) =>
    // A packaging read, not the master: packing users hold no masters.* code. With a variety, its
    // own layouts replace the crop's (0184).
    api<{ items: PackLayout[] }>(
      `/packaging/layouts${qs({ cropId, varietyId })}`
    ),
  /** The plant's active MP Types (NST rows), the same way: GET /packaging/mp-types?facilityId. */
  mpTypes: (facilityId: number | undefined) =>
    api<{ items: PackMpType[] }>(`/packaging/mp-types${qs({ facilityId })}`),
};
